import React, { useState, useEffect } from 'react';
import { MapContainer, Circle as LeafletCircle, Popup } from 'react-leaflet';
import { OpenStreetMapTiles } from '../components/OpenStreetMapTiles';
import 'leaflet/dist/leaflet.css';
import { Shield, MapPin } from 'lucide-react';
import { I270_POLYGON } from '../lib/i270';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminIdleHeatmap = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [message, setMessage] = useState('');

  const headers = () => ({ 'X-Admin-Key': adminKey });

  const load = async (n = days) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/idle-bubbles?days=${n}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setBubbles(data.bubbles || []);
      }
    } catch (err) {
      console.error('Failed to load idle bubbles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      });
      if (res.ok) { setAuthed(true); load(); }
      else setMessage('Incorrect admin key');
    } catch (err) {
      console.error('Admin auth failed:', err);
      setMessage('Unable to reach server. Try again.');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-sm">
          <div className="flex flex-col items-center mb-4">
            <Shield className="text-navy mb-2" size={32} />
            <h1 className="text-xl font-bold text-navy">Admin Access</h1>
          </div>
          <input
            type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key" autoFocus
            className="w-full px-3 py-2.5 border border-navy/20 rounded-lg mb-3 focus:border-navy focus:outline-none"
            data-testid="admin-key-input"
          />
          {message && <p className="text-red-600 text-sm mb-3">{message}</p>}
          <button type="submit" className="w-full bg-navy text-cream py-2.5 rounded-lg font-semibold hover:bg-navy/90" data-testid="login-btn">Enter</button>
        </form>
      </div>
    );
  }

  // Group nearby bubbles into hotspot clusters (~150m radius)
  const clusters = [];
  bubbles.forEach(b => {
    const found = clusters.find(c => {
      const dLat = Math.abs(c.lat - b.lat);
      const dLng = Math.abs(c.lng - b.lng);
      return dLat < 0.0015 && dLng < 0.0015;
    });
    if (found) {
      found.count += 1;
      found.totalHours += b.duration_hours || 0;
      found.drivers.add(b.driver_name || '');
    } else {
      clusters.push({ lat: b.lat, lng: b.lng, count: 1, totalHours: b.duration_hours || 0, drivers: new Set([b.driver_name || '']) });
    }
  });
  clusters.sort((a, b) => b.count - a.count);

  return (
    <div className="min-h-screen bg-cream">
      <section className="bg-navy py-6">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-cream flex items-center gap-2"><MapPin size={22} /> Idle Bubble Heatmap</h1>
          <div className="flex items-center gap-3 text-sm">
            <a href="/admin/geofences" className="text-cream/80 hover:text-cream underline">Geofences</a>
            <a href="/admin/advertisers" className="text-cream/80 hover:text-cream underline">Advertisers</a>
            <a href="/admin/leads" className="text-cream/80 hover:text-cream underline">Leads</a>
            <a href="/admin/manual-data" className="text-cream/80 hover:text-cream underline">Manual data</a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-xl px-4 py-3 flex items-center gap-3 border border-navy/10">
          <label className="text-sm font-semibold text-navy">Window:</label>
          {[7, 14, 30, 90].map(n => (
            <button
              key={n}
              onClick={() => { setDays(n); load(n); }}
              className={`px-3 py-1 text-sm font-semibold rounded-md ${days === n ? 'bg-navy text-cream' : 'bg-cream text-navy/70 border border-navy/10'}`}
              data-testid={`window-${n}`}
            >
              Last {n}d
            </button>
          ))}
          <span className="ml-auto text-xs text-navy/60">
            {loading ? 'Loading…' : `${bubbles.length} bubbles · ${clusters.length} clusters`}
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ height: 600 }}>
          <MapContainer center={[39.98, -82.99]} zoom={10} style={{ height: '100%', width: '100%' }}>
            <OpenStreetMapTiles mapId="admin-idle-heatmap" />
            {clusters.map((c, i) => {
              const radius = Math.min(2000, 200 + c.count * 60);
              return (
                <LeafletCircle
                  key={i}
                  center={[c.lat, c.lng]}
                  radius={radius}
                  pathOptions={{ color: '#7c3aed', fillColor: '#7c3aed', fillOpacity: 0.25, weight: 2 }}
                >
                  <Popup>
                    <strong>{c.count} idle event{c.count === 1 ? '' : 's'}</strong><br />
                    Total time parked: {c.totalHours.toFixed(2)} hrs<br />
                    Drivers seen: {c.drivers.size}<br />
                    {c.count >= 5 && <em style={{ color: '#9a3412' }}>⚠ Hotspot — investigate</em>}
                  </Popup>
                </LeafletCircle>
              );
            })}
          </MapContainer>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="font-bold text-navy mb-3">Top hotspots</h3>
          {clusters.slice(0, 10).length === 0 ? (
            <p className="text-sm text-navy/50">No idle bubbles in this window.</p>
          ) : (
            <table className="w-full text-sm" data-testid="clusters-table">
              <thead>
                <tr className="text-left text-navy/60 border-b border-navy/10">
                  <th className="py-2">#</th>
                  <th className="py-2">Location</th>
                  <th className="py-2 text-right">Events</th>
                  <th className="py-2 text-right">Total hours</th>
                  <th className="py-2 text-right">Distinct drivers</th>
                </tr>
              </thead>
              <tbody>
                {clusters.slice(0, 10).map((c, i) => (
                  <tr key={i} className="border-b border-navy/5">
                    <td className="py-2 text-navy/50">{i + 1}</td>
                    <td className="py-2 text-navy text-xs">{c.lat.toFixed(4)}, {c.lng.toFixed(4)}</td>
                    <td className="py-2 text-right font-semibold">{c.count}</td>
                    <td className="py-2 text-right">{c.totalHours.toFixed(1)}</td>
                    <td className="py-2 text-right">{c.drivers.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminIdleHeatmap;
